import React from 'react';
import { Phone, Mail, MapPin, Clock, MessageCircle, Car } from 'lucide-react';

const Contact: React.FC = () => {
  const contactInfo = [
    {
      icon: <Phone className="h-6 w-6" />,
      title: 'Atendimento ao Cliente',
      value: '(11) 2359-3715',
      link: 'tel:+551123593715'
    },
    {
      icon: <Phone className="h-6 w-6" />,
      title: 'Atendimento Técnico',
      value: '(11) 94714-1617',
      link: 'tel:+5511947141617'
    },
    {
      icon: <Mail className="h-6 w-6" />,
      title: 'Email',
      value: 'contato@willperformance.com.br',
      link: 'mailto:contato@willperformance.com.br'
    }
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="bg-gradient-to-r from-primary-600 to-primary-800 text-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h1 className="text-4xl md:text-6xl font-bold mb-6">
              ENTRE EM
              <span className="block text-yellow-400">CONTATO</span>
            </h1>
            <p className="text-xl md:text-2xl max-w-4xl mx-auto leading-relaxed">
              Estamos prontos para atender você e cuidar do seu veículo
            </p>
          </div>
        </div>
      </section>

      {/* Contact Information */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Contact Details */}
            <div>
              <h2 className="text-3xl font-bold text-gray-900 mb-8">
                Fale Conosco
              </h2>
              
              <div className="space-y-6 mb-8">
                {contactInfo.map((info, index) => (
                  <div 
                    key={index} 
                    className="flex items-center space-x-4 p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-all duration-300 transform hover:scale-105 group"
                    style={{ animationDelay: `${index * 0.1}s` }}
                  >
                    <div className="text-primary-600 group-hover:scale-110 transition-transform duration-300">
                      {info.icon}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-gray-600 group-hover:text-gray-700 transition-colors duration-300">{info.title}</p>
                      <a 
                        href={info.link}
                        className="text-sm sm:text-lg font-semibold text-gray-900 hover:text-primary-600 transition-colors duration-200 break-all"
                      >
                        {info.value}
                      </a>
                    </div>
                  </div>
                ))}
              </div>

              {/* WhatsApp CTA */}
              <div className="bg-green-50 border border-green-200 rounded-lg p-6 mb-8">
                <div className="flex items-center mb-4">
                  <MessageCircle className="h-8 w-8 text-green-600 mr-3" />
                  <h3 className="text-xl font-bold text-gray-900">
                    Atendimento pelo WhatsApp
                  </h3>
                </div>
                <p className="text-gray-700 mb-4">
                  Para um atendimento mais rápido e direto, entre em contato conosco pelo WhatsApp. 
                  Nossa equipe está pronta para esclarecer suas dúvidas e agendar seu atendimento.
                </p>
                <a
                  href="https://api.whatsapp.com/send?phone=551123593715"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center bg-green-500 hover:bg-green-600 text-white px-6 py-3 rounded-lg font-semibold transition-colors duration-200"
                >
                  <MessageCircle className="h-5 w-5 mr-2" />
                  Falar no WhatsApp
                </a>
              </div>

              {/* Towing Service */}
              <div className="bg-primary-50 border border-primary-200 rounded-lg p-6">
                <div className="flex items-center mb-4">
                  <Car className="h-8 w-8 text-primary-600 mr-3" />
                  <h3 className="text-xl font-bold text-gray-900">
                    Serviço de Guincho
                  </h3>
                </div>
                <p className="text-gray-700">
                  Caso seja necessário, contamos com guinchos para trazer seu veículo até nossa oficina. 
                  Entre em contato para solicitar o serviço.
                </p>
              </div>
            </div>

            {/* Location and Hours */}
            <div>
              <h2 className="text-3xl font-bold text-gray-900 mb-8">
                Localização e Horários
              </h2>

              {/* Address */}
              <div className="bg-white border border-gray-200 rounded-lg p-6 mb-6">
                <div className="flex items-start space-x-4">
                  <MapPin className="h-6 w-6 text-primary-600 mt-1" />
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">
                      Endereço
                    </h3>
                    <p className="text-gray-700 leading-relaxed">
                      R. Tiúba, 378<br />
                      Vila Nova Manchester<br />
                      São Paulo - SP<br />
                      CEP: 03443-015
                    </p>
                  </div>
                </div>
              </div>

              {/* Hours */}
              <div className="bg-white border border-gray-200 rounded-lg p-6 mb-6">
                <div className="flex items-start space-x-4">
                  <Clock className="h-6 w-6 text-primary-600 mt-1" />
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">
                      Horário de Funcionamento
                    </h3>
                    <div className="space-y-1 text-gray-700">
                      <p><strong>Segunda a Sexta:</strong> 8h às 18h</p>
                      <p><strong>Sábado:</strong> 8h às 13h</p>
                      <p><strong>Domingo:</strong> Fechado</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Google Maps */}
              <div className="bg-gray-200 rounded-lg h-64 overflow-hidden">
                <iframe
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3657.6996714178435!2d-46.53783152301615!3d-23.543301978812316!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x94ce5e6ee988af1b%3A0xd097a951673f8683!2sWill%20Performance!5e0!3m2!1sen!2sbr!4v1751608073904!5m2!1sen!2sbr"
                  width="100%"
                  height="100%"
                  style={{ border: 0 }}
                  allowFullScreen
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  title="Localização Will Performance"
                ></iframe>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Services Highlight */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Como Podemos Ajudar?
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Nossa equipe especializada está pronta para atender suas necessidades automotivas
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="text-center p-6 bg-white rounded-lg shadow-lg">
              <div className="bg-primary-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <Phone className="h-8 w-8 text-primary-600" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">
                Orçamentos
              </h3>
              <p className="text-gray-600">
                Solicite um orçamento sem compromisso para os serviços que seu veículo precisa
              </p>
            </div>

            <div className="text-center p-6 bg-white rounded-lg shadow-lg">
              <div className="bg-primary-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <Clock className="h-8 w-8 text-primary-600" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">
                Agendamentos
              </h3>
              <p className="text-gray-600">
                Agende seu atendimento no horário mais conveniente para você
              </p>
            </div>

            <div className="text-center p-6 bg-white rounded-lg shadow-lg">
              <div className="bg-primary-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <MessageCircle className="h-8 w-8 text-primary-600" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">
                Dúvidas
              </h3>
              <p className="text-gray-600">
                Tire suas dúvidas sobre nossos serviços e procedimentos
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-20 bg-primary-600 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">
            Pronto para Começar?
          </h2>
          <p className="text-xl mb-8 max-w-2xl mx-auto">
            Entre em contato conosco agora mesmo e descubra como podemos cuidar do seu veículo 
            com a qualidade e dedicação que você merece.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href="https://api.whatsapp.com/send?phone=551123593715"
              target="_blank"
              rel="noopener noreferrer"
              className="bg-green-500 hover:bg-green-600 text-white px-8 py-4 rounded-lg font-semibold text-lg transition-colors duration-200"
            >
              WhatsApp
            </a>
            <a
              href="tel:+551123593715"
              className="bg-white text-primary-600 hover:bg-gray-100 px-8 py-4 rounded-lg font-semibold text-lg transition-colors duration-200"
            >
              Ligar Agora
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;
